import paymentCloudImg from '@p2p/assets/img/payment-cloud.png';
import paymentMailImg from '@p2p/assets/img/payment-mail.png';
import { Button, CloseButton, Typography } from '@p2p/components';
import { MainContainer, usePaymentData, useTimeEndNotification } from '@p2p/entities/payment';
import { PaymentCancelModal, usePaymentTemplate } from '@p2p/entities/templates';
import {
  BillBlock,
  InfoBlock,
  PdfWarningModal,
  Section,
} from '@p2p/pages/MatchingDetailsPayment/components';
import styles from '@p2p/pages/MatchingDetailsPayment/MatchingDetailsPayment.module.scss';
import { useGetMerchantOrderQuery } from '@p2p/services';
import { MSP_PAYMENT_SENT_SESSION_KEY, PaymentType } from '@p2p/shared/constants';
import { useMount } from '@p2p/shared/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getProofPagePath } from '../ProofPayment.utils';

// Плательщик уже нажал «Деньги отправлены» — после перезагрузки (вернулся из
// банковского приложения) форма чека должна остаться активной (как на msp_page, P2P-2742).
const isPaymentSent = (orderId: string) => {
  try {
    return sessionStorage.getItem(MSP_PAYMENT_SENT_SESSION_KEY) === orderId;
  } catch {
    return false;
  }
};

const PAYMENT_TITLES: Record<string, string> = {
  [PaymentType.PAYMENT_TYPE_SBP]: 'Оплата через СБП',
  [PaymentType.PAYMENT_TYPE_C2C]: 'Оплата по номеру карты',
  [PaymentType.PAYMENT_TYPE_TRANSFER]: 'Оплата по номеру счета',
};

/**
 * P2P-2880: реквизиты + обязательная загрузка PDF-чека. Форма чека активна только
 * после «Деньги отправлены» (без запроса к бэку). После успешной отправки — поиск
 * платежа, вернуться сюда нельзя (см. resolveProofRoute).
 */
export const ProofDetailsPayment = () => {
  const { order, pageSettings } = usePaymentData();
  const { refetch } = useGetMerchantOrderQuery(order.id);
  const { setActiveIndex } = usePaymentTemplate();
  const navigate = useNavigate();
  const [isCheckPayment, setIsCheckPayment] = useState(() => isPaymentSent(order.id));
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Уведомление за 2 минуты — только пока «Деньги отправлены» не нажато.
  useTimeEndNotification({ enabled: !isCheckPayment });

  useMount(() => {
    setActiveIndex(2);
  });

  const pagePath = getProofPagePath(order.id);

  const handleCheckPayment = () => {
    try {
      sessionStorage.setItem(MSP_PAYMENT_SENT_SESSION_KEY, order.id);
    } catch {
      /* приватный режим / storage недоступен -- не критично */
    }

    setIsCheckPayment(true);
  };

  const handleUploaded = () => {
    // check_order вернёт receipt_uploaded=true — дальше страница держит плательщика на поиске.
    refetch();
    // replace: «Назад» с поиска платежа не должен вести обратно к реквизитам.
    navigate(`${pagePath}/search`, { replace: true });
  };

  const handleClose = () => {
    setIsCancelModalOpen(true);
  };

  return (
    <MainContainer className={styles.container} marginTop="2xl" maxWidth="700px">
      <div className={styles.header}>
        <Typography variant="h1">{PAYMENT_TITLES[order.requisite.payment_type] ?? ''}</Typography>
        <CloseButton onClick={handleClose} />
      </div>
      <div className={styles.sectionsContainer}>
        <Section
          title="Отправьте указанную сумму"
          logo={paymentMailImg}
          active
          checked={isCheckPayment}
        >
          <InfoBlock isShortView={isCheckPayment} handleShortView={handleCheckPayment} />
        </Section>
        <Section title={`Загрузите\nчек об оплате`} logo={paymentCloudImg} active={isCheckPayment}>
          <BillBlock
            isShortView={!isCheckPayment}
            onUploaded={handleUploaded}
            resetFileOnBadRequest
          />
        </Section>
      </div>
      <Button variant="outlined" onClick={handleClose} className={styles.cancelButton}>
        Отменить операцию
      </Button>
      {/* На proof_page «Важно» показывается всегда (на msp_page — по show_pdf_warning). */}
      <PdfWarningModal orderId={order.id} show />
      <PaymentCancelModal
        isActive={isCancelModalOpen}
        setIsActive={setIsCancelModalOpen}
        pageSettings={pageSettings}
        orderId={order.id}
        cancelPath={`${pagePath}/canceled`}
        orderStatus={order.status}
      />
    </MainContainer>
  );
};
